import React from 'react';
import classNames from 'classnames';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import { Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { Operator, TargetMetric } from '@app/shared/models/contracts/enums/shared-enums';
import SvgChevron from '@app/shared/icons/Chevron';
import { useLocale } from '@app/core/hooks/useLocale';
import { enumKeyByValue } from '@app/shared/helpers';
import { toPercentageUsingFormatter } from '@app/shared/formatters';
import TrancheSectionRow from './TrancheSectionRow';
import { useFocusMode } from '@app/shared/hooks/focus-mode';
import { IrrHurdleFocusSelections } from '@app/shared/hooks/focus-mode/focus-mode-types';

export type TrancheSectionCondition = {
  exitDates: {
    [
      exitDate: string
    ]: Pwerm2CalculationWaterfallCaseInstrumentTrancheConditionSponsorIrrCorkscrewPeriodDto[];
  };
  metric: keyof typeof TargetMetric;
  operator: keyof typeof Operator;
  testValue: number;
  equivalentHurdleValue: number;
};

export interface TrancheSectionProps {
  condition: TrancheSectionCondition;
  yearEntries: Record<string, number>;
  onToggle: () => void;
  isExpanded?: boolean;
  lastRowRef?: (el: HTMLTableRowElement | null) => void;
}

const PARENT_CLASSNAME = 'irr-hurdle-calculations-table';

const TrancheSection: React.FC<TrancheSectionProps> = ({
  condition,
  yearEntries,
  onToggle,
  isExpanded,
  lastRowRef,
}) => {
  const { l } = useLocale();
  const { isActive, focusType, selections } = useFocusMode();
  const focusModeType = isActive ? focusType : undefined;
  const irrSelections = selections as IrrHurdleFocusSelections | undefined;
  const navigatedSelectedYear = focusModeType ? irrSelections?.year : undefined;

  const years = Object.keys(yearEntries);

  const getYearHeaderColSpan = () => {
    const values = Object.values(yearEntries);
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum + (values.length - 1);
  };

  return (
    <>
      <tr>
        <th
          className={classNames(
            'table-primary__cell--header',
            'table-primary__cell--right',
            styles[`${PARENT_CLASSNAME}__cell--no-padding-right`]
          )}>
          <div
            className={styles['expand-toggle']}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SvgChevron
              onClick={focusModeType ? undefined : onToggle}
              className={classNames(
                isExpanded ? styles['chevron-expanded'] : styles['chevron-collapsed'],
                { [styles['greyed-out']]: focusModeType }
              )}
            />
            {l('_SponsorIrrPlaceholdersTitle', {
              operator:
                condition.operator === enumKeyByValue(Operator, Operator.GreaterThan)
                  ? '>'
                  : condition.operator === enumKeyByValue(Operator, Operator.LessThan)
                  ? '<'
                  : condition.operator === enumKeyByValue(Operator, Operator.GreaterThanOrEqual)
                  ? '>='
                  : condition.operator === enumKeyByValue(Operator, Operator.LessThanOrEqual)
                  ? '<='
                  : condition.operator,
              value: toPercentageUsingFormatter(condition.testValue),
            })}
          </div>
        </th>
        <td className="table-primary__cell--header" colSpan={getYearHeaderColSpan()} />
      </tr>
      {isExpanded && (
        <>
          <TrancheSectionRow
            label={l('_EventName')}
            valueKey="eventNarrative"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
          />
          <TrancheSectionRow
            label={l('_EventDate')}
            valueKey="periodStart"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isDate
          />
          <TrancheSectionRow
            label={l('_PeriodFrom')}
            valueKey="periodStart"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isDate
            isShaded
          />
          <TrancheSectionRow
            label={l('_PeriodTo')}
            valueKey="periodEnd"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isDate
            isShaded
          />
          <TrancheSectionRow
            label={l('_OpeningBalance')}
            valueKey="openingBalance"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isBold
            isNumber
          />
          <TrancheSectionRow
            label={l('_InfusionOrRedemptionNegative')}
            valueKey="additions"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isNumber
          />
          <TrancheSectionRow
            label={l('_AccruedReturn')}
            valueKey="accruedInterest"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isNumber
          />
          <TrancheSectionRow
            label={l('_CashInterest')}
            valueKey="cashPaid"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isNumber
          />
          <TrancheSectionRow
            label={l('_Dividends')}
            valueKey="dividends"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            isNumber
          />
          <TrancheSectionRow
            label={l('_ClosingBalance')}
            valueKey="closingBalance"
            exitDates={condition.exitDates}
            years={years}
            navigatedSelectedYear={navigatedSelectedYear}
            lastRowRef={lastRowRef}
            isBold
            isNumber
          />
        </>
      )}
    </>
  );
};
export default TrancheSection;
